/**
 * Estratégias de cache. Const object + type em vez de `enum` para respeitar
 * `erasableSyntaxOnly`, preservando uso como valor (`CachePolicy.CacheFirst`)
 * e como tipo (`CachePolicy`).
 */
export const CachePolicy = {
  /** Return cache if available, otherwise fetch from network */
  CacheFirst: 'cache-first',
  /** Always fetch from network, fall back to cache on failure */
  NetworkFirst: 'network-first',
  /** Always fetch from network, never use cache */
  NetworkOnly: 'network-only',
  /** Only use cache, never fetch from network */
  CacheOnly: 'cache-only',
  /** Return cache immediately, then revalidate in background */
  StaleWhileRevalidate: 'stale-while-revalidate',
} as const;

export type CachePolicy = (typeof CachePolicy)[keyof typeof CachePolicy];

export interface CacheEntry<T> {
  readonly data: T;
  readonly timestamp: number;
  readonly ttl: number;
}

export interface CacheConfig {
  /** Default TTL in milliseconds */
  defaultTtl: number;
  /** Maximum number of entries */
  maxEntries: number;
}

export interface Cache<T = unknown> {
  get(key: string): T | undefined;
  set(key: string, value: T, ttl?: number): void;
  remove(key: string): void;
  clear(): void;
  has(key: string): boolean;
}

const DEFAULT_CONFIG: CacheConfig = {
  defaultTtl: 5 * 60 * 1000, // 5 minutes
  maxEntries: 1000,
};

export class InMemoryCache<T = unknown> implements Cache<T> {
  private readonly store = new Map<string, CacheEntry<T>>();
  private readonly config: CacheConfig;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  get(key: string): T | undefined {
    const entry = this.store.get(key);
    if (!entry) return undefined;

    if (this.isExpired(entry)) {
      this.store.delete(key);
      return undefined;
    }

    return entry.data;
  }

  set(key: string, value: T, ttl?: number): void {
    if (this.store.size >= this.config.maxEntries && !this.store.has(key)) {
      this.evictOldest();
    }

    const entry: CacheEntry<T> = {
      data: value,
      timestamp: Date.now(),
      ttl: ttl ?? this.config.defaultTtl,
    };

    this.store.set(key, entry);
  }

  remove(key: string): void {
    this.store.delete(key);
  }

  clear(): void {
    this.store.clear();
  }

  has(key: string): boolean {
    const entry = this.store.get(key);
    if (!entry) return false;

    if (this.isExpired(entry)) {
      this.store.delete(key);
      return false;
    }

    return true;
  }

  get size(): number {
    return this.store.size;
  }

  private isExpired(entry: CacheEntry<T>): boolean {
    if (entry.ttl <= 0) return false; // TTL of 0 or less means no expiration
    return Date.now() - entry.timestamp > entry.ttl;
  }

  private evictOldest(): void {
    let oldestKey: string | undefined;
    let oldestTimestamp = Infinity;

    for (const [key, entry] of this.store) {
      if (entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey !== undefined) {
      this.store.delete(oldestKey);
    }
  }
}
